// Admin → Installed Devices. How many pieces are on a customer's premises,
// and which ones.
//
// ⚠️ THIS IS NOT THE USER ACCESS REPORT AND MUST NOT BE CONFUSED WITH IT. That
// one reads the mapping tables and answers "what can this USER reach"; this one
// reads the premises. Location 18 has 37 pieces installed and the User Access
// sheet for the user who owns it shows 13 — he is allotted one of its three
// bedrooms. Both numbers are correct answers to different questions, and only
// one of them is an installation count. The reasoning, and the endpoint, are in
// core/admin/installedReport.
//
// ── Three tabs ────────────────────────────────────────────────────────────
// BROWSE — User → Location → Room → Device, "All" at every level, ending in the
// device's details and its LIVE controls (a 4-touch shows its four switches).
// It asks the server for the PICKED user's view (core/admin/userBrowse), and
// the controls are the same cards the room grid uses, so a press publishes and
// the colour moves only when the device echoes — the rule everywhere else.
//
// REPORT — the printable handover sheet. A site is ONE request and lands
// instantly: the common case. "All locations" walks every location six at a
// time — around 117 requests — for the company-wide inventory, so it is chosen
// deliberately and shows progress while it runs. It is never the initial state.
//
// BUTTON ROWS — the devices whose buttons have no `OutputDetails` row, so their
// names cannot be saved at all (ui/shared/ButtonRowsPanel, 2026-10-23). It only
// ever inserts, and it shows the count before it writes.

import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box, Typography, AppBar, Toolbar, IconButton, Autocomplete, TextField,
  Alert, LinearProgress, Divider, Paper, Button, Tabs, Tab, CircularProgress,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { useApp } from '../../../core/state/AppContext';
import { getAllLocations } from '../../../core/api/api';
import { tokenFilter } from '../../../core/utils/search';
import ReportView, { ReportMeta } from '../../shared/ReportView';
import ReportActions from '../../shared/ReportActions';
import {
  loadSiteInstalled, loadEstateInstalled,
  siteInstalledReport, estateInstalledReport,
} from '../../../core/admin/installedReport';
import { getAllUsers, userLabel, userSearchText } from '../../../core/devices/deviceAdmin';
import {
  locationsOfUser, roomsOfLocation, roomsOfLocations, devicesOfRoom, devicesOfRooms,
  locationName, roomName, deviceName,
} from '../../../core/admin/userBrowse';
import { deviceTypeCaption, deviceCardWidth } from '../../../core/devices/deviceMeta';
import DeviceControlCard from '../components/DeviceControlCard';
import ButtonRowsPanel from '../../shared/ButtonRowsPanel';

// The "every location" choice, modelled as an option in the same picker rather
// than a separate switch: it is the same question at a different scope, and a
// mode toggle beside a picker is one control too many.
const ALL = { LocationID: '__all__', LocationName: 'All locations' };

const labelOf = (l) => (l === ALL || l.LocationID === ALL.LocationID
  ? 'All locations'
  : l.LocationWithName || l.LocationName || `Location ${l.LocationID}`);

const searchText = (l) => `${labelOf(l)} ${l.LocationID}`;

// ── Browse ────────────────────────────────────────────────────────────────

// The "All …" option at each level. One object per level so
// isOptionEqualToValue can tell them apart by id.
const ALL_LOCATIONS = { LocationID: '__all__', LocationName: 'All locations' };
const ALL_ROOMS = { RoomID: '__all__', RoomName: 'All rooms' };
const ALL_DEVICES = { UniqueNumber: '__all__', DeviceName: 'All devices' };

const isAll = (x) => !!x && (x.LocationID === '__all__' || x.RoomID === '__all__' || x.UniqueNumber === '__all__');

// Where a row sits, for the flat "All" lists: "Home › Hall".
const roomPath = (r) => `${locationName(r.location)} › ${roomName(r)}`;
const devicePath = (d) => `${roomPath(d.room)} › ${deviceName(d)}`;

function Picker({
  label, options, value, onChange, getLabel, keyOf, searchOf, disabled, loading, width = 300,
}) {
  return (
    <Autocomplete
      size="small"
      sx={{ width: { xs: '100%', sm: width } }}
      options={options}
      value={value}
      disabled={disabled}
      loading={loading}
      onChange={(_, v) => onChange(v)}
      getOptionLabel={getLabel}
      isOptionEqualToValue={(o, v) => keyOf(o) === keyOf(v)}
      filterOptions={tokenFilter(searchOf || getLabel)}
      // ⚠️ NOT DECORATION — supplies the option KEY. Names repeat at every
      // level ("Home", "Hall", "s1"), and MUI keys on the label unless told
      // otherwise, which drops and duplicates rows as the list re-renders.
      renderOption={(props, option) => (
        <li {...props} key={keyOf(option)}>{getLabel(option)}</li>
      )}
      renderInput={(p) => (
        <TextField
          {...p}
          label={label}
          InputProps={{
            ...p.InputProps,
            endAdornment: (
              <>
                {loading ? <CircularProgress color="inherit" size={14} /> : null}
                {p.InputProps.endAdornment}
              </>
            ),
          }}
        />
      )}
    />
  );
}

// One line of the selected device's details.
function Fact({ k, v }) {
  return (
    <Box sx={{ display: 'flex', gap: 1, fontSize: 13 }}>
      <Typography sx={{ fontSize: 13, color: 'text.secondary', minWidth: 110 }}>{k}</Typography>
      <Typography sx={{ fontSize: 13, fontWeight: 600, wordBreak: 'break-all' }}>{v ?? '—'}</Typography>
    </Box>
  );
}

function DeviceDetail({ device }) {
  const heard = device.LastUpdatedDateTime || device.lastUpdatedDateTime
    || device.NetworkLastUpdatedDateTime || '—';
  return (
    <Paper variant="outlined" sx={{ p: 1.5, mb: 1.5 }}>
      <Typography sx={{ fontSize: 15, fontWeight: 700, mb: 0.5 }}>{deviceName(device)}</Typography>
      <Fact k="Type" v={deviceTypeCaption(device)} />
      <Fact k="Unique number" v={device.UniqueNumber} />
      <Fact k="Location" v={locationName(device.location)} />
      <Fact k="Room" v={roomName(device.room)} />
      <Fact k="Last status" v={device.Status || '—'} />
      <Fact k="Last heard" v={heard} />
    </Paper>
  );
}

// A grid of live control cards — the same cards as the room grid, so a
// 4-touch is four switches and a fan is a dial. `subtitle` says where each
// piece is, since an "All" list mixes rooms.
function ControlGrid({ devices, showPath }) {
  if (!devices.length) {
    return <Alert severity="info">No devices here for this user.</Alert>;
  }
  return (
    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1.5, alignItems: 'flex-start' }}>
      {devices.map((d) => (
        // Fixed width on a desk; on a phone (the mobile shell routes here too)
        // a 380 px meter card must shrink to the screen rather than scroll it.
        <Box
          key={`${d.room.RoomID}-${d.UniqueNumber}`}
          sx={{ flex: `0 1 ${deviceCardWidth(d.DeviceID)}px`, maxWidth: '100%', minWidth: 0 }}
        >
          <DeviceControlCard
            device={d}
            subtitle={showPath ? roomPath(d.room) : undefined}
          />
        </Box>
      ))}
    </Box>
  );
}

function BrowseTab() {
  const [users, setUsers] = useState([]);
  const [user, setUser] = useState(null);
  const [locations, setLocations] = useState([]);
  const [location, setLocation] = useState(null);
  const [rooms, setRooms] = useState([]);
  const [room, setRoom] = useState(null);
  const [devices, setDevices] = useState([]);
  const [device, setDevice] = useState(null);
  const [loading, setLoading] = useState('');   // which level is loading
  const [error, setError] = useState('');

  // Every fetch is stamped; a reply from a superseded pick is dropped, so
  // changing the user while its rooms are still loading cannot fill the room
  // list with the previous user's rooms.
  const seq = useRef(0);
  const begin = () => { seq.current += 1; return seq.current; };
  const current = (n) => seq.current === n;

  useEffect(() => {
    getAllUsers()
      .then((res) => setUsers(Array.isArray(res.data) ? res.data : []))
      .catch(() => setError('Could not load the user list.'));
  }, []);

  const pickUser = useCallback(async (u) => {
    const n = begin();
    setUser(u); setLocations([]); setLocation(null);
    setRooms([]); setRoom(null); setDevices([]); setDevice(null); setError('');
    if (!u) return;
    setLoading('locations');
    try {
      const list = await locationsOfUser(u.UserId);
      if (current(n)) setLocations(list);
    } catch (e) {
      if (current(n)) setError("Could not load this user's locations.");
    } finally {
      if (current(n)) setLoading('');
    }
  }, []);

  const pickLocation = useCallback(async (l) => {
    const n = begin();
    setLocation(l); setRooms([]); setRoom(null); setDevices([]); setDevice(null); setError('');
    if (!l || !user) return;
    setLoading('rooms');
    try {
      const list = isAll(l)
        ? await roomsOfLocations(user.UserId, locations)
        : await roomsOfLocation(user.UserId, l);
      if (current(n)) setRooms(list);
    } catch (e) {
      if (current(n)) setError('Could not load the rooms.');
    } finally {
      if (current(n)) setLoading('');
    }
  }, [user, locations]);

  const pickRoom = useCallback(async (r) => {
    const n = begin();
    setRoom(r); setDevices([]); setDevice(null); setError('');
    if (!r || !user) return;
    setLoading('devices');
    try {
      const list = isAll(r)
        ? await devicesOfRooms(user.UserId, rooms)
        : await devicesOfRoom(user.UserId, r);
      if (current(n)) setDevices(list);
    } catch (e) {
      if (current(n)) setError('Could not load the devices.');
    } finally {
      if (current(n)) setLoading('');
    }
  }, [user, rooms]);

  const manyLocations = isAll(location);
  const manyRooms = isAll(room);
  const one = device && !isAll(device);
  const shown = one ? [device] : devices;

  return (
    <>
      <Box sx={{
        p: 1, display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap', flexShrink: 0,
      }}
      >
        <Picker
          label="User"
          options={users}
          value={user}
          onChange={pickUser}
          getLabel={(u) => `${userLabel(u)}${u.MobileNumber ? ` · ${u.MobileNumber}` : ''}`}
          keyOf={(u) => String(u.UserId)}
          searchOf={userSearchText}
        />
        <Picker
          label="Location"
          options={user ? [ALL_LOCATIONS, ...locations] : []}
          value={location}
          onChange={pickLocation}
          getLabel={(l) => (isAll(l) ? l.LocationName : locationName(l))}
          keyOf={(l) => String(l.LocationID)}
          disabled={!user || loading === 'locations'}
          loading={loading === 'locations'}
          width={260}
        />
        <Picker
          label="Room"
          options={location ? [ALL_ROOMS, ...rooms] : []}
          value={room}
          onChange={pickRoom}
          getLabel={(r) => (isAll(r) ? r.RoomName : (manyLocations ? roomPath(r) : roomName(r)))}
          keyOf={(r) => String(r.RoomID)}
          disabled={!location || loading === 'rooms'}
          loading={loading === 'rooms'}
          width={260}
        />
        <Picker
          label="Device"
          options={room ? [ALL_DEVICES, ...devices] : []}
          value={device}
          onChange={(d) => setDevice(d)}
          getLabel={(d) => (isAll(d) ? d.DeviceName : (manyRooms ? devicePath(d) : deviceName(d)))}
          keyOf={(d) => (isAll(d) ? '__all__' : `${d.room.RoomID}-${d.UniqueNumber}`)}
          searchOf={(d) => (isAll(d) ? d.DeviceName : `${devicePath(d)} ${d.UniqueNumber}`)}
          disabled={!room || loading === 'devices'}
          loading={loading === 'devices'}
          width={300}
        />
      </Box>

      {error && <Alert severity="error" sx={{ mx: 1, mb: 1 }}>{error}</Alert>}

      <Box sx={{ px: 1, pb: 1, flex: 1, minHeight: 0, overflowY: 'auto' }}>
        {!user && (
          <Alert severity="info">
            Pick a user to browse what they can reach — location by location, room by
            room, down to one device and its working switches.
          </Alert>
        )}
        {user && !location && !loading && (
          <Alert severity="info">
            {locations.length
              ? `${locations.length} location${locations.length === 1 ? '' : 's'} — pick one, or All locations.`
              : 'This user has no locations.'}
          </Alert>
        )}
        {location && !room && !loading && (
          <Alert severity="info">
            {rooms.length
              ? `${rooms.length} room${rooms.length === 1 ? '' : 's'} — pick one, or All rooms.`
              : 'No rooms here.'}
          </Alert>
        )}
        {loading === 'devices' && (
          <Box sx={{ pb: 1 }}>
            <Typography sx={{ fontSize: 12, mb: 0.5 }}>Reading devices…</Typography>
            <LinearProgress />
          </Box>
        )}
        {room && !loading && (
          <>
            {one && <DeviceDetail device={device} />}
            <Typography sx={{ fontSize: 12, color: 'text.secondary', mb: 1 }}>
              {one
                ? 'Live controls — a press publishes; the button changes only when the device answers.'
                : `${shown.length} device${shown.length === 1 ? '' : 's'}`}
            </Typography>
            <ControlGrid devices={shown} showPath={manyRooms} />
          </>
        )}
      </Box>
    </>
  );
}

// ── Report ────────────────────────────────────────────────────────────────

function ReportTab() {
  const { companyName } = useApp();

  const [locations, setLocations] = useState([]);
  const [choice, setChoice] = useState(null);
  const [report, setReport] = useState(null);
  const [progress, setProgress] = useState(null);
  const [error, setError] = useState('');

  // Bumped on every read. A stale estate walk keeps firing progress callbacks
  // for a minute after the operator has picked something else — it has no way
  // to know — so both the progress line and the result are gated on it.
  const runId = useRef(0);
  // Read by collectEstateDevices between locations, so leaving the page does not
  // leave 117 requests running behind it.
  const stopped = useRef(false);
  useEffect(() => () => { stopped.current = true; }, []);

  useEffect(() => {
    getAllLocations()
      .then((res) => setLocations(Array.isArray(res.data) ? res.data : []))
      .catch(() => setError('Could not load the location list.'));
  }, []);

  const run = useCallback(async (pick) => {
    if (!pick) { setReport(null); setProgress(null); return; }
    runId.current += 1;
    const mine = runId.current;
    stopped.current = false;
    const mineStill = () => runId.current === mine && !stopped.current;

    setReport(null);
    setError('');
    setProgress({ label: 'Reading…' });

    try {
      const estate = pick.LocationID === ALL.LocationID;
      const devices = estate
        ? await loadEstateInstalled(
          (p) => { if (mineStill()) setProgress(p); },
          () => !mineStill(),
        )
        : await loadSiteInstalled(pick);

      if (!mineStill()) return;
      setReport(estate
        ? estateInstalledReport({ devices })
        : siteInstalledReport({ location: pick, devices }));
    } catch (e) {
      if (mineStill()) setError('That location could not be read.');
    } finally {
      if (runId.current === mine) setProgress(null);
    }
  }, []);

  // The letterhead is a session fact — core builds the model and knows nothing
  // about who is signed in.
  const sheet = report ? { ...report, brand: companyName || undefined } : null;
  const pct = progress && progress.total
    ? Math.round((progress.done / progress.total) * 100)
    : null;

  return (
    <>
      <Box sx={{
        p: 1, display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap', flexShrink: 0,
      }}
      >
        <Autocomplete
          size="small"
          sx={{ width: 360 }}
          options={[ALL, ...locations]}
          value={choice}
          onChange={(_, v) => { setChoice(v); run(v); }}
          getOptionLabel={labelOf}
          isOptionEqualToValue={(o, v) => String(o.LocationID) === String(v.LocationID)}
          filterOptions={tokenFilter(searchText)}
          // ⚠️ NOT DECORATION — this supplies the option KEY. Location names
          // repeat ("Home" appears dozens of times), and MUI keys on the label
          // unless given something better, which drops and duplicates rows as
          // the list re-renders on each keystroke.
          renderOption={(props, option) => (
            <li {...props} key={String(option.LocationID)}>{labelOf(option)}</li>
          )}
          renderInput={(p) => (
            <TextField {...p} placeholder="Pick a location, or All locations…" />
          )}
        />
        {choice && !progress && (
          <Button size="small" onClick={() => run(choice)}>Re-read</Button>
        )}
        <Box sx={{ flex: 1 }} />
        {sheet && <ReportActions report={sheet} onError={setError} />}
      </Box>

      {error && <Alert severity="error" sx={{ mx: 1, mb: 1 }}>{error}</Alert>}

      {progress && (
        <Box sx={{ px: 1, pb: 1, flexShrink: 0 }}>
          <Typography sx={{ fontSize: 12, mb: 0.5 }}>{progress.label || 'Reading…'}</Typography>
          <LinearProgress
            variant={pct == null ? 'indeterminate' : 'determinate'}
            value={pct == null ? undefined : pct}
          />
          <Typography color="text.secondary" sx={{ fontSize: 10.5, mt: 0.5 }}>
            One request per location, six at a time. Leaving this page stops it.
          </Typography>
        </Box>
      )}

      {!choice && !progress && (
        <Alert severity="info" sx={{ mx: 1 }}>
          Pick a location to count what is installed on the premises — every
          piece, whoever it is mapped to. This is not the same as User Access,
          which lists only what one user has been allotted.
        </Alert>
      )}

      {sheet && (
        <Paper
          variant="outlined"
          sx={{
            m: 1, mt: 0, p: 1, flex: 1, minHeight: 0, overflowY: 'auto',
          }}
        >
          <Typography sx={{ fontSize: 15, fontWeight: 700, mb: 0.5 }}>{sheet.title}</Typography>
          <ReportMeta report={sheet} />
          <Divider sx={{ my: 1 }} />
          <ReportView report={sheet} />
        </Paper>
      )}
    </>
  );
}

// ── Page ──────────────────────────────────────────────────────────────────

export default function InstalledDevicesScreen() {
  const { isSuperAdmin } = useApp();
  const navigate = useNavigate();
  const [tab, setTab] = useState('browse');

  if (!isSuperAdmin) {
    return <Box sx={{ p: 3 }}><Alert severity="error">Super Admin only.</Alert></Box>;
  }

  return (
    <Box sx={{ height: '100%', minHeight: 0, display: 'flex', flexDirection: 'column' }}>
      <AppBar position="static" elevation={2}>
        <Toolbar variant="dense">
          <IconButton edge="start" color="inherit" onClick={() => navigate('/home')}>
            <ArrowBackIcon />
          </IconButton>
          <Typography variant="h6" sx={{ flex: 1, fontSize: 16 }}>Installed Devices</Typography>
          <Tabs
            value={tab}
            onChange={(_, v) => setTab(v)}
            textColor="inherit"
            indicatorColor="secondary"
            sx={{ minHeight: 40, '& .MuiTab-root': { minHeight: 40, fontSize: 13 } }}
          >
            <Tab value="browse" label="Browse" />
            <Tab value="report" label="Report" />
            {/* The devices whose buttons have no row to rename — see
                ui/shared/ButtonRowsPanel. */}
            <Tab value="rows" label="Button rows" />
          </Tabs>
        </Toolbar>
      </AppBar>
      {tab === 'browse' && <BrowseTab />}
      {tab === 'report' && <ReportTab />}
      {tab === 'rows' && <ButtonRowsPanel />}
    </Box>
  );
}
